<ng-container *transloco="let t">
  <div class="mx-auto w-full max-w-screen-md py-10">
    <div class="mb-10">
      <div class="text-2xl font-semibold tracking-tight text-gray-800">{{ t("page-title.billing") }}</div>
      <div class="text-sm font-medium text-gray-500">{{ t("billing-settings.billing-page-subtitle") }}</div>
    </div>

    <div class="mb-5">
      <ISelectMenu
        [value]="selectedOrganization()"
        (valueChange)="changeOrganization($event)"
        by="id"
        [(searchQuery)]="searchQuery"
        fitTriggerWidth
      >
        <ISelectTrigger class="flex w-full">
          <ISelectValue>
            <div *iSelectValue="let value">{{ value?.organizationName }}</div>
          </ISelectValue>
        </ISelectTrigger>
        <ISelectContent>
          @for (organization of filteredOrganizations(); track organization.id) {
            <ISelectOption [value]="organization">
              {{ organization.organizationName }}
            </ISelectOption>
          }
        </ISelectContent>
      </ISelectMenu>
    </div>
    @if (selectedOrganization()) {
      <div class="mb-5 pb-3">
        <div class="flex items-center gap-7">
          <div>
            <div class="text-sm font-medium text-gray-800">{{ t("account-setting.organization") }}</div>
            <div class="text-sm text-gray-500">{{ selectedOrganization()!.organizationName }}</div>
          </div>
        </div>

        <div class="my-7 flex flex-col gap-4">
          @if (selectedOrganization()!.organization.billingPlan && locale()) {
            <IPlanCard [organizationGroup]="selectedOrganization()!" [locale]="locale()" [defaultCurrency]="currency()">
              <div cardActions class="flex items-center">
                @if (canMigrateToParentOrganization()) {
                  <button iButton variant="secondary" (click)="openMigrateToParentOrganizationModal()">
                    {{ t("billing-settings.migrate_to", [parentOrgName()]) }}
                  </button>
                  <app-onboarding-cta class="p-4"></app-onboarding-cta>
                }
              </div>
            </IPlanCard>
            <app-billable-ad-spend-card
              [organization]="selectedOrganization()!.organization"
              [nextInvoiceDate]="nextInvoice()?.nextCycleDate"
            />
          }
        </div>

        <hr class="mb-5 h-px w-full border-gray-200" />
      </div>
      @if (canDisplayBillingInfo() || (selectedOrganization()?.customer && !isSelfService())) {
        <div>
          <div class="text-foreground text-xl font-semibold tracking-tight">
            {{ t("billing-settings.billing_details") }}
          </div>
          <div class="type-body-medium text-muted-foreground mb-4">
            {{ t("billing-settings.billing_details_subtitle") }}
          </div>
          @if (selectedOrganization()!.customer) {
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              @if (canDisplayBillingInfo()) {
                <div iCard direction="vertical">
                  <div iCardHeader class="flex items-start justify-between gap-4">
                    <div>
                      <div class="type-body-medium text-foreground">{{ t("billing-settings.address") }}</div>
                      <div class="type-label text-muted-foreground">{{ t("billing-settings.address_subtitle") }}</div>
                    </div>
                    <button
                      iButton
                      variant="secondary"
                      size="icon-sm"
                      [iTooltip]="t('billing-settings.update_billing_info')"
                      (click)="openUpdateBillingDetailsModal()"
                    >
                      <IIcon name="edit" />
                    </button>
                  </div>
                  <div class="flex flex-col px-4 sm:px-6">
                    <IBillingInfo
                      [customer]="selectedOrganization()!.customer!"
                      [stripePublicKey]="stripePublishableKey"
                    />
                  </div>
                </div>
              }
              @if (!isSelfService()) {
                <IPaymentMethods
                  [customer]="selectedOrganization()!.customer!"
                  (addIban)="openAddIbanModal()"
                  (addCreditCard)="openAddCreditCardModal()"
                />
              }
              @if (canDisplayBillingInfo()) {
                <div iCard direction="vertical" class="md:col-span-2">
                  <div iCardHeader class="flex items-center justify-between gap-4 pb-0 sm:pb-0">
                    <div>
                      <div class="type-body-medium text-foreground">{{ t("subscription-card.po_number") }}</div>
                      <div class="type-label text-muted-foreground">{{ t("billing-settings.po_number_subtitle") }}</div>
                    </div>
                    <div class="type-body flex items-center gap-3">
                      @if (poNumber()) {
                        <span class="text-foreground">{{ poNumber() }}</span>
                      } @else {
                        <span class="text-subtle-foreground">{{ t("billing-settings.not_provided") }}</span>
                      }
                      <button
                        iButton
                        variant="secondary"
                        size="icon-sm"
                        [iTooltip]="t('billing-settings.update_po_number')"
                        (click)="openUpdatePoNumberModal()"
                      >
                        <IIcon name="edit" />
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="flex flex-col gap-4">
              <div iCard direction="vertical">
                <div iCardHeader class="flex flex-col items-start gap-4">
                  <div>
                    <div class="type-body-medium text-foreground">{{ t("billing-settings.no_billing_details") }}</div>
                    <div class="type-label text-muted-foreground">
                      {{ t("billing-settings.no_billing_details_subtitle") }}
                    </div>
                  </div>
                  <button iButton (click)="openUpdateBillingDetailsModal()">
                    <IIcon name="add" />
                    {{ t("billing-settings.enter_billing_details") }}
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }

      @if (!isSelfService()) {
        <div class="mt-10">
          <div class="mb-3 text-xl font-semibold tracking-tight text-gray-800">
            {{ t("billing-settings.invoices_history") }}
          </div>
          @if (this.nextInvoice()?.nextCycleDate) {
            <div class="mb-3 text-sm font-medium text-gray-500">
              {{
                t("billing-settings.next_invoice_date", {
                  date: this.nextInvoice()?.nextCycleDate | date: "longDate" : undefined : locale(),
                })
              }}
            </div>
          }
          <div class="mx-auto max-h-[25rem] w-[95rem] max-w-full">
            <IBillingDocumentsTable
              [customer]="selectedOrganization()!.customer!"
              [organizationId]="selectedOrganization()!.organization.organizationId"
              [locale]="locale()"
              [dailyAdSpendFeesByInvoiceId]="feesByInvoiceId()"
              (invoiceInfoClick)="openInvoiceDetailsModal($event)"
            />
          </div>
        </div>
      } @else if (this.nextInvoice()?.nextCycleDate) {
        <!-- in case there is no invoice but a next invoice is coming  -->
        <div class="mb-3 text-sm font-medium text-gray-500">
          {{
            t("billing-settings.next_invoice_date", {
              date: this.nextInvoice()?.nextCycleDate,
            })
          }}
        </div>
      }
    }
  </div>

  <ng-template #updatePoNumberModal>
    <div>
      <input iInput size="sm" name="poNumber" [ngModel]="poNumber()" (ngModelChange)="poNumberInput.set($event)" />

      <div class="flex justify-end gap-2 pt-4">
        <button iButton variant="secondary" (click)="updatePoNumberModalRef?.close()">{{ t("common.cancel") }}</button>
        <button
          iButton
          variant="destructive"
          [disabled]="poNumberInput() === null"
          [loading]="poNumberLoading()"
          (click)="updatePoNumber(true)"
        >
          {{ t("common.delete") }}
        </button>
        <button iButton [disabled]="poNumberInput() === null" [loading]="poNumberLoading()" (click)="updatePoNumber()">
          {{ t("common.save") }}
        </button>
      </div>
    </div>
  </ng-template>

  <ng-template #updateBillingDetailsModal>
    <div>
      @if (customerLoading()) {
        <div class="absolute inset-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white/50">
          <ISpinner [display]="true" />
        </div>
      }

      <IBillingInfoForm
        [stripePublishableKey]="stripePublishableKey"
        [formValue]="selectedOrganization()!.customer!"
        [requiredCard]="false"
        [updateBillingMode]="true"
        (billingFormValueChange)="updateBillingDetails($event)"
        (onCancel)="modalRef?.close()"
      />
    </div>
  </ng-template>
</ng-container>
